import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import { withConversationState } from '../../utils/navigationHelpers.ts'
import { X } from 'lucide-react'
import { useConversationalAgent } from '../../contexts/ConversationalAgentContext.tsx'
import { getDesktopSuggestions } from '../Dashboard/DashboardHeader/dashboardActionsConfig.tsx'
import ConversationWindow from '../Dashboard/DashboardHeader/ConversationalMode/ConversationWindow/ConversationWindow.tsx'
import ConversationInput from '../Dashboard/DashboardHeader/ConversationalMode/ConversationInput/ConversationInput.tsx'
import AIAgentOrbLoader from '../Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx'
import { matchRoute, getRouteLabel } from '../../routes/routeConfig.ts'
import { FloatingAgentMessageContext } from './FloatingAgentMessageContext.tsx'
import { getProfileContextualSuggestion } from './profileTabPrompts.ts'
import styles from './FloatingAgent.module.scss'

const FloatingAgent: React.FC = () => {
  const location = useLocation()
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const {
    currentSession,
    sendMessage,
    isProcessing,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    userId,
    userData,
    missingFields,
    readyToExecute,
    hasPendingImage,
    imageUploadAcknowledged,
    pendingImagePreview,
    storePendingImage,
    clearPendingImage
  } = useConversationalAgent()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const navigateFromConversation = useMemo(() => withConversationState(navigate), [navigate])

  const allSuggestions = useMemo(
    () => getDesktopSuggestions(navigateFromConversation, userId, userData, dispatch),
    [navigateFromConversation, userId, userData, dispatch]
  )

  const messages = currentSession?.messages ?? []

  const routeMatch = useMemo(() => {
    const matches = matchRoute(location.pathname)
    if (matches.length === 0) return null
    const { node, params } = matches[0]
    if (node.key === 'dashboard') return null
    return { node, params }
  }, [location.pathname])

  const contextualEmpty = useMemo(() => {
    if (!routeMatch) return null
    if (routeMatch.node.key === 'perfil') {
      const searchParams = new URLSearchParams(location.search)
      const tabParam = searchParams.get('tab')
      const profileId = routeMatch.params?.id
      const isOwnProfile =
        profileId != null &&
        userId != null &&
        String(profileId) === String(userId)
      const suggestion = getProfileContextualSuggestion(tabParam, isOwnProfile)
      return suggestion ? { label: suggestion.label, prompt: suggestion.prompt } : null
    }
    if (!routeMatch.node.suggestionPrompt) return null
    return {
      label: getRouteLabel(routeMatch.node, routeMatch.params),
      prompt: routeMatch.node.suggestionPrompt
    }
  }, [routeMatch, location.search, userId])

  const handleSend = async () => {
    if ((!inputValue.trim() && !hasPendingImage) || isProcessing || !canSendMessage) return
    const message = inputValue.trim()
    setInputValue('')
    await sendMessage(message || 'Imagen cargada')
  }

  const showImageUpload = missingFields?.includes('image_uploaded') ?? false

  const handleConfirmExecution = async () => {
    if (isProcessing) return
    setInputValue('')
    await sendMessage('', { confirmExecution: true })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSuggestionChipClick = () => {
    if (!contextualEmpty?.prompt || isProcessing || !canSendMessage) return
    sendMessage(contextualEmpty.prompt)
  }

  return (
    <>
      {!isPanelOpen && (
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setIsPanelOpen(true)}
          aria-label="Abrir asistente con IA"
          title="Abrir asistente (hacé clic)"
        >
          <AIAgentOrbLoader status="idle" className={styles.triggerOrb} />
        </button>
      )}

      <AnimatePresence>
        {isPanelOpen && (
          <motion.div
            className={styles.panel}
            role="dialog"
            aria-label="Asistente con IA"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ transformOrigin: 'bottom right' }}
          >
            <div className={styles.header}>
            <span className={styles.headerTitle}>Asistente IA de Edify</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setIsPanelOpen(false)}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
          <div className={styles.content}>
            <div className={styles.messagesWrap}>
              {messages.length === 0 && !isProcessing ? (
                contextualEmpty ? (
                  <div className={styles.emptyStateContextual}>
                    <p className={styles.emptyStateLabel}>
                      Estás en {contextualEmpty.label}.
                    </p>
                    <button
                      type="button"
                      className={styles.suggestionChip}
                      onClick={handleSuggestionChipClick}
                      disabled={!canSendMessage}
                    >
                      {contextualEmpty.prompt}
                    </button>
                    <p className={styles.emptyStateHint}>
                      Usá la sugerencia o escribí lo que quieras.
                    </p>
                  </div>
                ) : (
                  <div className={styles.emptyStateGeneric}>
                    <p className={styles.emptyStateText}>
                      Hacé tu primera pregunta y te ayudo a encontrar lo que necesitás.
                    </p>
                  </div>
                )
              ) : (
                <FloatingAgentMessageContext.Provider value={true}>
                  <ConversationWindow
                    messages={messages}
                    onClose={() => setIsPanelOpen(false)}
                    isLoading={isProcessing}
                    matchedSuggestion={null}
                    allSuggestions={allSuggestions}
                    hasReachedLimit={hasReachedLimit}
                    remainingMessages={remainingMessages}
                    limit={limit}
                    userData={userData}
                  />
                </FloatingAgentMessageContext.Provider>
              )}
            </div>
            <div className={styles.inputWrap}>
              <ConversationInput
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                onKeyPress={handleKeyPress}
                disabled={isProcessing || !canSendMessage}
                placeholder={
                  canSendMessage
                    ? 'Escribí tu mensaje...'
                    : 'Has alcanzado el límite de mensajes'
                }
                showImageUpload={showImageUpload}
                hasPendingImage={hasPendingImage}
                imageUploadAcknowledged={imageUploadAcknowledged}
                pendingImagePreview={pendingImagePreview}
                onImageSelect={storePendingImage}
                onImageClear={clearPendingImage}
                readyToExecute={readyToExecute}
                onConfirmExecution={readyToExecute ? handleConfirmExecution : undefined}
              />
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default FloatingAgent
